import { type ReactNode, useMemo } from "react";
import { useOutToday } from "@/features/leave/hooks/use-out-today";
import { useActiveWorkspace } from "@/shared/hooks/use-active-workspace";
import type { AwayInfo } from "@/shared/presence/away-label";
import { PresenceProvider } from "@/shared/presence/presence.context";

/**
 * Where "who is out" is fetched, once per workspace.
 *
 * ## Why it lives in `app/` rather than beside the context
 *
 * `PresenceProvider` is in `shared/`, and shared may not import a feature
 * (G3). The data comes from the leave module's `out-today`. `app/` is the one
 * layer allowed to reach both, so the wiring lands here and the shared side
 * stays a plain map. The relationship board's `reputationSlot` solves the same
 * problem the same way.
 *
 * ## Why the whole workspace and not the visible rows
 *
 * `out-today` is one request that returns everybody currently away, which is
 * a handful of people on any normal day. Fetching per visible person would be
 * sixty requests on the directory to learn that fifty five of them are at
 * work.
 *
 * It is also the only leave route every member may call: `LEAVE_VIEW_OUT_TODAY`
 * is member tier, deliberately, and the payload carries names and dates and
 * nothing else. Mounting it globally therefore widens nobody's access. The
 * balance and calendar routes would, which is why the badge is built on this
 * one and stops where it stops.
 *
 * ## Staleness, and why "stale" is not enough on its own
 *
 * Five minutes, because somebody's leave being approved mid-afternoon is not
 * information a reader needs within thirty seconds.
 *
 * But a `staleTime` only says when data MAY be refetched, never that anything
 * will ask. This query mounts at the app layout and never unmounts across
 * navigation, and the client default is `refetchOnWindowFocus: false`, so
 * without the two options below nothing would ever fetch it a second time.
 *
 * The failure that causes is not a slightly old dot. `today` is pinned to the
 * date the SERVER measured on, so a tab left open overnight would keep
 * phrasing every label against yesterday: "back in 19 days" off by one, and
 * "away until tomorrow" for somebody already at their desk. Focus covers the
 * laptop reopened in the morning; the interval covers the tab that was never
 * blurred.
 */
export function PresenceBoundary({ children }: { children: ReactNode }): ReactNode {
  const { workspace, isResolved } = useActiveWorkspace();
  // Gated on `isResolved` rather than on the workspace being present: the hook
  // hands back a mock workspace for an anonymous session and for a slug the
  // auth payload has not caught up with, and firing this at either produces a
  // 401 or a 404 for a badge nobody is waiting on.
  const query = useOutToday(isResolved ? workspace.id : undefined, undefined, {
    staleTimeMs: 5 * 60_000,
    refetchOnFocus: true,
    refetchIntervalMs: 15 * 60_000,
  });

  const away = useMemo<ReadonlyMap<string, AwayInfo>>(() => {
    const map = new Map<string, AwayInfo>();
    for (const item of query.data?.items ?? []) {
      map.set(item.membershipId, { fromDate: item.fromDate, toDate: item.toDate });
    }
    return map;
  }, [query.data]);

  return (
    <PresenceProvider
      away={away}
      // The date the SERVER measured on, echoed back on the response. Today is
      // a workspace-local question, and the reader's own clock is the wrong
      // one to phrase "away until tomorrow" against when the two disagree.
      today={query.data?.date ?? ""}
      isReady={Boolean(query.data)}
    >
      {children}
    </PresenceProvider>
  );
}
